import { useState } from "react";
import ManagerLayout from "@/components/layout/ManagerLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BookingStatus } from "@/lib/types";
import { Calendar, Search, Filter, Download, Clock, CheckCircle, XCircle, AlertTriangle, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { apiRequest } from '@/lib/api';

export default function ManagerBookingsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("upcoming");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Fetch bookings from API
  const { data: bookingsData = [], isLoading } = useQuery({
    queryKey: ['manager/bookings'],
    enabled: !!user && user.role === 'manager',
    queryFn: async () => {
      return await apiRequest('GET', 'manager/bookings');
    },
    onError: (err: any) => {
      toast({
        title: "Error loading bookings",
        description: err.message || "Failed to load bookings data",
        variant: "destructive",
      });
    }
  });

  const getStatusBadge = (status: BookingStatus) => {
    switch (status) {
      case BookingStatus.PENDING:
        return (
          <Badge variant="outline" className="flex items-center gap-1 text-yellow-600 bg-yellow-50 border-yellow-200">
            <Clock className="h-3 w-3" />
            <span>Pending</span>
          </Badge>
        );
      case BookingStatus.CONFIRMED:
        return (
          <Badge variant="outline" className="flex items-center gap-1 text-green-600 bg-green-50 border-green-200">
            <CheckCircle className="h-3 w-3" />
            <span>Confirmed</span>
          </Badge>
        );
      case BookingStatus.COMPLETED:
        return (
          <Badge variant="outline" className="flex items-center gap-1 text-blue-600 bg-blue-50 border-blue-200">
            <CheckCircle className="h-3 w-3" />
            <span>Completed</span>
          </Badge>
        );
      case BookingStatus.CANCELLED:
        return (
          <Badge variant="outline" className="flex items-center gap-1 text-red-600 bg-red-50 border-red-200">
            <XCircle className="h-3 w-3" />
            <span>Cancelled</span>
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            <span>Unknown</span>
          </Badge>
        );
    }
  };

  // Process bookings data
  const processingBookings = (bookings: any[] = []) => {
    // Group bookings by status
    const grouped = {
      upcoming: [] as any[],
      completed: [] as any[],
      cancelled: [] as any[]
    };
    
    const today = new Date();
    
    bookings.forEach(booking => {
      const checkIn = new Date(booking.checkIn);
      const checkOut = new Date(booking.checkOut);
      
      if (booking.status === BookingStatus.CANCELLED) {
        grouped.cancelled.push(booking);
      } else if (booking.status === BookingStatus.COMPLETED || 
                (booking.status === BookingStatus.CONFIRMED && checkOut < today)) {
        grouped.completed.push(booking);
      } else {
        grouped.upcoming.push(booking);
      }
    });
    
    return grouped;
  };
  
  const bookings = processingBookings(bookingsData);
  
  const filterBookings = (bookingList: any[] = []) => {
    return bookingList.filter(booking => {
      // Apply search filter
      const propertyTitle = booking.property?.title || booking.propertyTitle || '';
      const guestName = booking.guest?.name || booking.guestName || '';
      const guestEmail = booking.guest?.email || booking.guestEmail || '';
      
      const searchMatch = 
        searchTerm === "" || 
        propertyTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guestName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guestEmail.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Apply status filter
      const statusMatch = 
        statusFilter === "all" || 
        booking.status.toLowerCase() === statusFilter.toLowerCase();
      
      return searchMatch && statusMatch;
    });
  };

  const getBookingsForTab = (tab: string) => {
    switch (tab) {
      case "upcoming":
        return [...(bookings.upcoming || [])].sort((a, b) => 
          new Date(a.checkIn).getTime() - new Date(b.checkIn).getTime());
      case "completed":
        return [...(bookings.completed || [])].sort((a, b) => 
          new Date(b.checkOut).getTime() - new Date(a.checkOut).getTime());
      case "cancelled":
        return bookings.cancelled || [];
      case "all":
        return [...(bookings.upcoming || []), ...(bookings.completed || []), ...(bookings.cancelled || [])];
      default:
        return [];
    }
  };

  const filteredBookings = filterBookings(getBookingsForTab(activeTab));

  return (
    <ManagerLayout>
      <div className="container py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-1">Bookings</h1>
            <p className="text-muted-foreground">Manage your property bookings</p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center gap-4">
            <Button variant="outline" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Sync Calendar</span>
            </Button>
            <Button variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              <span>Export</span>
            </Button>
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search by property, guest or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="upcoming">Upcoming ({(bookings?.upcoming || []).length})</TabsTrigger>
            <TabsTrigger value="completed">Completed ({(bookings?.completed || []).length})</TabsTrigger>
            <TabsTrigger value="cancelled">Cancelled ({(bookings?.cancelled || []).length})</TabsTrigger>
            <TabsTrigger value="all">All Bookings ({(bookings?.upcoming || []).length + (bookings?.completed || []).length + (bookings?.cancelled || []).length})</TabsTrigger>
          </TabsList>
        </Tabs>

        <Card>
          <CardHeader className="px-6">
            <CardTitle>Booking Details</CardTitle>
            <CardDescription>Manage and view all your property bookings</CardDescription>
          </CardHeader>
          <CardContent className="px-6">
            {isLoading ? (
              <div className="flex justify-center items-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredBookings.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Guest</TableHead>
                    <TableHead>Check-in</TableHead>
                    <TableHead>Check-out</TableHead>
                    <TableHead>Guests</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Booked On</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell className="font-medium">
                        {booking.property?.title || booking.propertyTitle || `Property #${booking.propertyId}`}
                      </TableCell>
                      <TableCell>
                        <div>{booking.guest?.name || booking.guestName || "Guest"}</div>
                        <div className="text-xs text-muted-foreground">{booking.guest?.email || booking.guestEmail}</div>
                      </TableCell>
                      <TableCell>{format(new Date(booking.checkIn), 'MMM d, yyyy')}</TableCell>
                      <TableCell>{format(new Date(booking.checkOut), 'MMM d, yyyy')}</TableCell>
                      <TableCell>{booking.guestCount}</TableCell>
                      <TableCell>
                        {booking.totalAmount ? 
                          new Intl.NumberFormat('en-AE', {
                            style: 'currency',
                            currency: 'AED',
                            minimumFractionDigits: 0,
                          }).format(booking.totalAmount) : 
                          'N/A'
                        }
                      </TableCell>
                      <TableCell>{getStatusBadge(booking.status)}</TableCell>
                      <TableCell>{format(new Date(booking.createdAt), 'MMM d, yyyy')}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="h-8" onClick={() => {
                          window.location.href = `/bookings/${booking.id}/confirmation`;
                        }}>
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No bookings found matching your filters.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ManagerLayout>
  );
}